import { useEffect, useState } from "react";
import { adminListCollection, adminCreateCollection, adminUpdateCollection, adminDeleteCollection, adminToggleVisibility } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";
import ImageUploader from "@/components/ImageUploader";
import { Plus, Pencil, Trash2, Eye, EyeOff, ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";

const inp = "w-full border border-line bg-parchment px-3 py-2.5 text-sm text-ink focus:border-saffron focus:outline-none";
const EMPTY = { slug: "", title: "", deva_title: "", author: "", excerpt: "", cover_image: "", tags: "", status: "draft", body: "" };
const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function AdminJournal() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => { setLoading(true); adminListCollection("posts").then(setItems).finally(() => setLoading(false)); };
  useEffect(load, []);

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (p) => setEditing({ ...EMPTY, ...p, tags: Array.isArray(p.tags) ? p.tags.join(", ") : (p.tags || "") });

  const save = async () => {
    const e = editing;
    if (!e.title) { toast.error("Title is required."); return; }
    const data = {
      ...e,
      slug: e.slug || slugify(e.title),
      tags: (e.tags || "").split(",").map((t) => t.trim()).filter(Boolean),
      published_at: e.status === "published" ? (e.published_at || new Date().toISOString()) : (e.published_at || null),
    };
    delete data.id; delete data.created_at;
    setBusy(true);
    try {
      if (e.id) await adminUpdateCollection("posts", e.id, data);
      else await adminCreateCollection("posts", data);
      toast(e.id ? "Article updated" : "Article created");
      setEditing(null); load();
    } catch (err) { toast.error("Save failed", { description: err.response?.data?.detail || err.message }); }
    finally { setBusy(false); }
  };

  const remove = async (p) => { if (!window.confirm(`Delete "${p.title}"?`)) return; await adminDeleteCollection("posts", p.id); toast("Deleted"); load(); };
  const toggle = async (p) => { const r = await adminToggleVisibility("posts", p.id); setItems((x) => x.map((i) => i.id === p.id ? { ...i, hidden: r.hidden } : i)); };

  if (editing) {
    const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.value });
    return (
      <div className="max-w-3xl">
        <button onClick={() => setEditing(null)} className="mb-4 inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-saffron"><ArrowLeft className="h-4 w-4" /> Back to articles</button>
        <h1 className="mb-6 font-serif-latin text-3xl text-ink">{editing.id ? "Edit article" : "New article"}</h1>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div><label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Title</label><input data-testid="post-title" value={editing.title} onChange={set("title")} className={inp} /></div>
            <div><label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Devanagari title</label><input value={editing.deva_title} onChange={set("deva_title")} className={`${inp} font-deva-body`} /></div>
            <div><label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Slug (optional)</label><input data-testid="post-slug" value={editing.slug} onChange={set("slug")} placeholder="auto from title" className={inp} /></div>
            <div><label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Author</label><input value={editing.author} onChange={set("author")} className={inp} /></div>
            <div className="sm:col-span-2"><ImageUploader label="Cover image" value={editing.cover_image} onChange={(v) => setEditing({ ...editing, cover_image: v })} testid="post-cover" /></div>
            <div className="sm:col-span-2"><label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Excerpt</label><textarea rows={2} value={editing.excerpt} onChange={set("excerpt")} className={inp} /></div>
            <div><label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Tags (comma-separated)</label><input value={editing.tags} onChange={set("tags")} className={inp} /></div>
            <div><label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Status</label>
              <select data-testid="post-status" value={editing.status} onChange={set("status")} className={inp}>
                <option value="draft">Draft</option><option value="published">Published</option>
              </select>
            </div>
          </div>
          <div><label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Article body</label>
            <RichTextEditor value={editing.body} onChange={(html) => setEditing({ ...editing, body: html })} placeholder="Write your article…" />
          </div>
          <button data-testid="post-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-2.5 text-sm font-medium text-white hover:bg-[var(--saffron-dark)] disabled:opacity-60"><Save className="h-4 w-4" /> {busy ? "Saving…" : "Save article"}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <div><h1 className="font-serif-latin text-3xl text-ink">Patrika</h1><p className="mt-1 text-sm text-ink-3">{loading ? "…" : `${items.length} articles`}</p></div>
        <button data-testid="new-post" onClick={openNew} className="inline-flex items-center gap-2 bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white hover:bg-[var(--saffron-dark)]"><Plus className="h-4 w-4" /> New Article</button>
      </div>
      {loading ? <p className="py-16 text-center text-ink-3">…</p> : (
        <div className="overflow-hidden border border-line bg-surface">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-line bg-surface-alt/50 text-xs uppercase tracking-wider text-ink-3"><tr><th className="px-4 py-3">Title</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr></thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} data-testid={`post-row-${p.slug}`} className="border-b border-line last:border-0">
                  <td className="px-4 py-3"><span className={`font-deva-body ${p.hidden ? "text-ink-3 line-through" : "text-ink"}`}>{p.title}</span><span className="block text-xs text-ink-3">{p.slug}</span></td>
                  <td className="px-4 py-3"><span className={p.status === "published" ? "text-saffron" : "text-ink-3"}>{p.status}</span></td>
                  <td className="px-4 py-3"><div className="flex items-center justify-end gap-2">
                    <button onClick={() => toggle(p)} title={p.hidden ? "Show" : "Hide"} className="p-2 text-ink-2 hover:text-saffron">{p.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                    <button data-testid={`post-edit-${p.slug}`} onClick={() => openEdit(p)} className="p-2 text-ink-2 hover:text-saffron"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(p)} className="p-2 text-ink-2 hover:text-[var(--saffron-dark)]"><Trash2 className="h-4 w-4" /></button>
                  </div></td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={3} className="px-4 py-16 text-center text-ink-3">No articles yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
